import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { ArrowLeft, Users, Shield, Search, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('users');
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Role Management State
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [phoneForRole, setPhoneForRole] = useState('');

  useEffect(() => {
    // Check if user is admin
    if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }

    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich with donation and report stats
      const enrichedUsers = await Promise.all(
        response.data.map(async (u) => {
          try {
            // Get donations
            const donationsRes = await axios.get(`${API}/donations/user/${u.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Get reports
            const reportsRes = await axios.get(`${API}/reports?user_id=${u.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            return {
              ...u,
              totalDonated: donationsRes.data.reduce((sum, d) => sum + d.amount, 0),
              donationCount: donationsRes.data.length,
              reportCount: reportsRes.data.length
            };
          } catch (error) {
            return { ...u, totalDonated: 0, donationCount: 0, reportCount: 0 };
          }
        })
      );

      setUsers(enrichedUsers);
      setFilteredUsers(enrichedUsers);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchPhone.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(u => 
      u.phone?.includes(searchPhone) ||
      u.name?.toLowerCase().includes(searchPhone.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    
    if (!phoneForRole || !newRole) {
      toast.error('Please enter phone number and select role');
      return;
    }

    // Find user by phone
    const targetUser = users.find(u => u.phone === phoneForRole);
    if (!targetUser) {
      toast.error('User not found with this phone number');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${targetUser.id}/assign-role`,
        { role: newRole, permissions: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Role ${newRole} assigned successfully`);
      setPhoneForRole('');
      setNewRole('');
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign role');
    }
  };

  const downloadUserReport = () => {
    // Create CSV
    const csv = [
      ['Name', 'Phone', 'Role', 'Total Donated (₹)', 'Donations', 'Reports', 'Joined', 'Last Login'],
      ...filteredUsers.map(u => [
        u.name || 'N/A',
        u.phone,
        u.role,
        (u.totalDonated / 100).toFixed(2),
        u.donationCount,
        u.reportCount,
        new Date(u.created_at).toLocaleDateString(),
        u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_report.csv';
    a.click();
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Home
        </button>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            Admin Dashboard
          </h1>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="users">
                <Users size={18} className="mr-2" />
                User Management
              </TabsTrigger>
              <TabsTrigger value="roles">
                <Shield size={18} className="mr-2" />
                Role Management
              </TabsTrigger>
            </TabsList>

            {/* User Management Tab */}
            <TabsContent value="users">
              <div className="space-y-6">
                {/* Search */}
                <div className="flex gap-4">
                  <div className="flex-1 flex gap-2">
                    <Input
                      placeholder="Search by phone or name..."
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch}>
                      <Search size={18} className="mr-2" />
                      Search
                    </Button>
                  </div>
                  <Button onClick={downloadUserReport} variant="outline">
                    <Download size={18} className="mr-2" />
                    Download Report
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold">{filteredUsers.length}</div>
                    <div className="text-sm text-gray-600">Total Users</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold">
                      {filteredUsers.filter(u => u.role !== 'user').length}
                    </div>
                    <div className="text-sm text-gray-600">Admin/Moderators</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold">
                      ₹{(filteredUsers.reduce((sum, u) => sum + (u.totalDonated || 0), 0) / 100).toFixed(0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Donated</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold">
                      {filteredUsers.reduce((sum, u) => sum + (u.reportCount || 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Reports</div>
                  </div>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Donated</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reports</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{u.name || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm font-mono">{u.phone}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              u.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                              u.role === 'moderator' ? 'bg-blue-100 text-blue-800' :
                              u.role === 'finance_manager' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            ₹{((u.totalDonated || 0) / 100).toFixed(2)} ({u.donationCount || 0})
                          </td>
                          <td className="px-4 py-3 text-sm">{u.reportCount || 0}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredUsers.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No users found
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Role Management Tab */}
            <TabsContent value="roles">
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Available Roles</h3>
                  <ul className="text-sm space-y-1 text-gray-700">
                    <li><strong>super_admin:</strong> Full system access</li>
                    <li><strong>moderator:</strong> Approve reports, manage comments, assign vendors</li>
                    <li><strong>finance_manager:</strong> Manage refunds and view invoices</li>
                    <li><strong>support_agent:</strong> Handle support tickets and comments</li>
                    <li><strong>auditor:</strong> Read-only access to all data</li>
                    <li><strong>user:</strong> Regular user (default)</li>
                  </ul>
                </div>

                <form onSubmit={handleAssignRole} className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium mb-2">User Phone Number</label>
                    <Input
                      type="tel"
                      placeholder="+919876543210"
                      value={phoneForRole}
                      onChange={(e) => setPhoneForRole(e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the user's registered phone number</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Assign Role</label>
                    <Select value={newRole} onValueChange={setNewRole} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="moderator">Moderator</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                        <SelectItem value="finance_manager">Finance Manager</SelectItem>
                        <SelectItem value="support_agent">Support Agent</SelectItem>
                        <SelectItem value="auditor">Auditor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="w-full">
                    Assign Role
                  </Button>
                </form>

                {/* Recent Role Changes */}
                <div>
                  <h3 className="font-semibold mb-4">Admin & Moderators</h3>
                  <div className="space-y-2">
                    {users
                      .filter(u => u.role !== 'user')
                      .map(u => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium">{u.name || 'Unnamed'}</div>
                            <div className="text-sm text-gray-600">{u.phone}</div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            u.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                            u.role === 'moderator' ? 'bg-blue-100 text-blue-800' :
                            u.role === 'finance_manager' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {u.role}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
